import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Star, Trash2, Upload } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import {
  AdminBanner,
  AdminButton,
  AdminCard,
  AdminField,
  AdminPageHead,
  AdminSpinner,
  Drawer,
  adminInputClass,
} from "@/components/admin/AdminUi";
import MediaPicker from "@/components/admin/MediaPicker";

const BLANK_PHOTO = {
  image: "",
  thumbnail: "",
  caption: "",
  year: String(new Date().getFullYear()),
  display_order: 0,
  featured: false,
  published: true,
};

export function EmeraldGallery() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [banner, setBanner] = useState({ status: "idle", message: "" });
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkRef = useRef(null);

  const load = useCallback(() => {
    api
      .get("/admin/emerald/gallery")
      .then((r) => setItems(r.data || []))
      .catch((e) => {
        setItems([]);
        setBanner({ status: "error", message: apiError(e) });
      });
  }, []);

  useEffect(load, [load]);

  const payloadOf = (row) => ({
    image: row.image,
    thumbnail: row.thumbnail || "",
    caption: row.caption || "",
    year: row.year || "",
    display_order: Number(row.display_order) || 0,
    featured: Boolean(row.featured),
    published: Boolean(row.published),
  });

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/emerald/gallery/${editing.id}`, payloadOf(editing));
      else await api.post("/admin/emerald/gallery", payloadOf(editing));
      setBanner({ status: "ok", message: "Gallery photograph saved. It is live on the public page." });
      setEditing(null);
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const patch = async (row, changes) => {
    try {
      await api.put(`/admin/emerald/gallery/${row.id}`, payloadOf({ ...row, ...changes }));
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this photograph from the Emerald Honours gallery?")) return;
    try {
      await api.delete(`/admin/emerald/gallery/${row.id}`);
      setBanner({ status: "ok", message: "Photograph deleted." });
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (!items || target < 0 || target >= items.length) return;
    const a = items[index];
    const b = items[target];
    try {
      await api.put(`/admin/emerald/gallery/${a.id}`, payloadOf({ ...a, display_order: b.display_order ?? target }));
      await api.put(`/admin/emerald/gallery/${b.id}`, payloadOf({ ...b, display_order: a.display_order ?? index }));
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const bulkUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setBulkBusy(true);
    let added = 0;
    let order = (items || []).reduce((max, row) => Math.max(max, row.display_order ?? 0), 0);
    try {
      for (const file of files) {
        const body = new FormData();
        body.append("file", file);
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.post("/admin/media", body, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        order += 1;
        // eslint-disable-next-line no-await-in-loop
        await api.post("/admin/emerald/gallery", {
          ...BLANK_PHOTO,
          image: `/api/media/${data.id}/file`,
          display_order: order,
        });
        added += 1;
      }
      setBanner({ status: "ok", message: `${added} photograph(s) uploaded and published.` });
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    } finally {
      setBulkBusy(false);
      if (bulkRef.current) bulkRef.current.value = "";
      load();
    }
  };

  const set = (k) => (e) => setEditing((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="cms-emerald-gallery">
      <Seo title="Emerald Honours Gallery | Bahamas Kidney Association CMS" noindex />
      <AdminPageHead
        title="Emerald Honours Gallery"
        subtitle="Event photographs shown in the “A Night to Remember” gallery on the public Emerald Honours page."
      >
        {can("emerald.content") && (
          <div className="flex flex-wrap gap-2">
            <input
              ref={bulkRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={bulkUpload}
              className="hidden"
              data-testid="cms-emerald-gallery-bulk-input"
            />
            <AdminButton
              variant="ghost"
              testId="cms-emerald-gallery-bulk"
              onClick={() => bulkRef.current?.click()}
              disabled={bulkBusy}
            >
              <Upload className="h-4 w-4" /> {bulkBusy ? "Uploading…" : "Upload Multiple Photos"}
            </AdminButton>
            <AdminButton
              variant="emerald"
              testId="cms-emerald-gallery-add"
              onClick={() =>
                setEditing({
                  ...BLANK_PHOTO,
                  display_order: (items || []).reduce((m, r) => Math.max(m, r.display_order ?? 0), 0) + 1,
                })
              }
            >
              <Plus className="h-4 w-4" /> Add Photo
            </AdminButton>
          </div>
        )}
      </AdminPageHead>

      <AdminBanner
        status={banner.status}
        message={banner.message}
        onDismiss={() => setBanner({ status: "idle", message: "" })}
      />

      <AdminCard>
        {items === null ? (
          <AdminSpinner />
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500" data-testid="cms-emerald-gallery-empty">
            No gallery photographs yet. The public page is currently showing the approved fallback
            event photographs — upload photos here and they take over automatically.
          </div>
        ) : (
          <div className="grid gap-4 p-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="cms-emerald-gallery-grid">
            {items.map((row, index) => (
              <div
                key={row.id}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                data-testid={`cms-emerald-gallery-row-${row.id}`}
              >
                <div className="relative h-44 bg-slate-100">
                  <img
                    src={mediaUrl(row.thumbnail || row.image)}
                    alt={row.caption || "Emerald Honours event photograph"}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  {row.featured && (
                    <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-amber-400 px-2.5 py-1 text-[11px] font-extrabold uppercase text-amber-950">
                      <Star className="h-3 w-3" /> Featured
                    </span>
                  )}
                  <span
                    className={`absolute right-2 top-2 rounded-full px-2.5 py-1 text-[11px] font-extrabold uppercase ${
                      row.published ? "bg-emerald-600 text-white" : "bg-slate-500 text-white"
                    }`}
                  >
                    {row.published ? "Published" : "Unpublished"}
                  </span>
                </div>

                <div className="space-y-3 p-4">
                  <p className="line-clamp-2 text-sm font-semibold text-bka-navy">
                    {row.caption || <span className="text-slate-400">No caption</span>}
                  </p>
                  <p className="text-xs text-slate-500">
                    Year {row.year || "—"} · Order {row.display_order ?? 0}
                  </p>

                  <div className="flex flex-wrap gap-1.5">
                    <AdminButton
                      variant="ghost"
                      className="px-3 py-1.5 text-xs"
                      testId={`cms-emerald-gallery-edit-${row.id}`}
                      onClick={() => setEditing(row)}
                    >
                      Edit
                    </AdminButton>
                    {can("emerald.content") && (
                      <>
                        <AdminButton
                          variant="ghost"
                          className="px-3 py-1.5 text-xs"
                          testId={`cms-emerald-gallery-feature-${row.id}`}
                          onClick={() => patch(row, { featured: !row.featured })}
                        >
                          {row.featured ? "Unfeature" : "Make Featured"}
                        </AdminButton>
                        <AdminButton
                          variant="ghost"
                          className="px-3 py-1.5 text-xs"
                          testId={`cms-emerald-gallery-publish-${row.id}`}
                          onClick={() => patch(row, { published: !row.published })}
                        >
                          {row.published ? "Unpublish" : "Publish"}
                        </AdminButton>
                        <button
                          type="button"
                          onClick={() => move(index, -1)}
                          aria-label="Move earlier"
                          data-testid={`cms-emerald-gallery-up-${row.id}`}
                          className="flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-bka-emerald hover:text-bka-emerald"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => move(index, 1)}
                          aria-label="Move later"
                          data-testid={`cms-emerald-gallery-down-${row.id}`}
                          className="flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-bka-emerald hover:text-bka-emerald"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(row)}
                          aria-label="Delete photograph"
                          data-testid={`cms-emerald-gallery-delete-${row.id}`}
                          className="flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </AdminCard>

      <Drawer
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Gallery Photo" : "Add Gallery Photo"}
        testId="cms-emerald-gallery-drawer"
      >
        {editing && (
          <div className="space-y-5">
            <AdminField label="Photograph" required>
              <MediaPicker
                kind="images"
                label=""
                value={editing.image}
                onChange={(url) => setEditing((f) => ({ ...f, image: url }))}
                buttonLabel="Choose or Upload Photograph"
                testId="cms-emerald-gallery-image-picker"
              />
            </AdminField>

            <AdminField label="Caption (optional)" htmlFor="eg-caption">
              <input
                id="eg-caption"
                data-testid="cms-emerald-gallery-caption"
                value={editing.caption || ""}
                onChange={set("caption")}
                className={adminInputClass}
                placeholder="Honourees celebrating at the Emerald Soirée"
              />
            </AdminField>

            <div className="grid gap-5 sm:grid-cols-2">
              <AdminField label="Event Year" htmlFor="eg-year">
                <input
                  id="eg-year"
                  data-testid="cms-emerald-gallery-year"
                  value={editing.year || ""}
                  onChange={set("year")}
                  className={adminInputClass}
                  placeholder="2026"
                />
              </AdminField>
              <AdminField label="Display Order" htmlFor="eg-order" hint="Lower numbers appear first.">
                <input
                  id="eg-order"
                  type="number"
                  data-testid="cms-emerald-gallery-order"
                  value={editing.display_order ?? 0}
                  onChange={set("display_order")}
                  className={adminInputClass}
                />
              </AdminField>
            </div>

            <label className="flex items-center gap-3 text-sm font-semibold text-bka-navy">
              <input
                type="checkbox"
                checked={Boolean(editing.featured)}
                onChange={(e) => setEditing((f) => ({ ...f, featured: e.target.checked }))}
                data-testid="cms-emerald-gallery-featured"
                className="h-4 w-4 rounded border-slate-300"
              />
              Featured — show this as the large photograph first
            </label>

            <label className="flex items-center gap-3 text-sm font-semibold text-bka-navy">
              <input
                type="checkbox"
                checked={Boolean(editing.published)}
                onChange={(e) => setEditing((f) => ({ ...f, published: e.target.checked }))}
                data-testid="cms-emerald-gallery-published"
                className="h-4 w-4 rounded border-slate-300"
              />
              Published — visible on the public Emerald Honours page
            </label>

            <AdminButton
              variant="emerald"
              testId="cms-emerald-gallery-save"
              onClick={save}
              disabled={!editing.image}
            >
              Save Photograph
            </AdminButton>
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default EmeraldGallery;
